import React, { useEffect, useState } from "react";

import { InfoSmIcon, Modal } from "@packages/ds";
import { useIntl } from "react-intl";

import config from "console_config";
import { PLATFORM_FEATURE_BLACKFIRE } from "Constants/constants";
import { Button, ButtonWrapper, IconControl } from "ds/Button";
import * as Sticker from "ds/Sticker";
import { Link } from "ds/Toast";
import {
  deploymentHasLanguageSelector,
  loadDeploymentCurrent
} from "Reducers/environment/deployment";
import { organizationSelector } from "Reducers/organization/organization";
import { gitProjectSelector } from "Reducers/project/project";
import useBlackFireSellableStatus from "src/organization/pages/project/containers/ProjectOverview/BlackfireCard/useBlackFireSellableStatus";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import AmountField from "../AmountField";
import * as S from "../styles";

import PromptEnablObservabilitySuite from "./PromptEnablObservabilitySuite";
import { StickerText } from "./style";

import type {
  OrganizationSubscription,
  SubscriptionEstimateComplex
} from "@packages/client";

const actionLinksPro = [
  { href: "links.blackfire.features", title: "learnmore" },
  {
    href: "links.platform.contact",
    title: "contact_sales"
  }
];

const getEnterpriseActionLinks = (isPurchased: boolean) =>
  isPurchased
    ? [
        { href: "links.observability-suite-features", title: "learnmore" },
        {
          href: "links.observability-suite-blackfire",
          title: "docs"
        }
      ]
    : [
        { href: "links.observability-suite-features", title: "learnmore" },
        {
          href: "links.platform.contact",
          title: "contact_sales"
        }
      ];

const getEnterpriseInfo = (isPurchased: boolean) =>
  isPurchased
    ? "plans.blackfire.enterprize_addon_included_info"
    : "plans.blackfire.enterprize_addon_acquire_info";

type BlackfireAddonPropType = {
  subscription?: OrganizationSubscription;
  estimate?: SubscriptionEstimateComplex;
  blackfire?: string | null;
  onProductChange: (productName?: string) => void;
};

const BlackfireAddon = (props: BlackfireAddonPropType) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [openAddPromptModal, setOpenAddPromptModal] = useState(false);

  const { subscription, blackfire, onProductChange, estimate } = props;
  const { isPurchased, isEnterprise, canPurchaseFromConsole } =
    useBlackFireSellableStatus(subscription);

  const estimateCost = isEnterprise
    ? estimate?.observability_suite
    : estimate?.blackfire;

  const organization = useAppSelector(state =>
    organizationSelector(state, {
      organizationId: subscription?.organization_id || subscription?.owner
    })
  );

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId: organization?.name,
      projectId: subscription?.project_id
    })
  );

  const setAddBlackfire = (productName?: string) => {
    setOpenAddPromptModal(false);
    onProductChange(productName);
  };

  const organizationId = organization?.name;
  const projectId = subscription?.project_id;
  const environmentId = project?.default_branch;

  useEffect(() => {
    if (organizationId && projectId && environmentId) {
      dispatch(
        loadDeploymentCurrent({
          organizationId,
          projectId,
          environmentId,
          settings: {
            escapeLoading: true
          }
        })
      );
    }
  }, [dispatch, organizationId, environmentId, projectId]);

  const hasSupportLanguage = useAppSelector(state =>
    deploymentHasLanguageSelector(state, {
      environmentId,
      organizationId,
      projectId,
      languages: config.CUSTOM_BLACKFIRE_CARD_LANGUAGE
    })
  );

  if (!hasSupportLanguage) return null;

  const handleFeatureToggle = () => {
    if (hasSupportLanguage) {
      onProductChange(blackfire ? "" : PLATFORM_FEATURE_BLACKFIRE);
    } else {
      blackfire ? onProductChange("") : setOpenAddPromptModal(true);
    }
  };

  const enterpriseIsPurchased = !!(isPurchased && isEnterprise);
  const blackfireAddedButNotPurchased = blackfire && !isPurchased;

  return (
    <>
      <S.Heading>
        <S.Info>
          <S.MainLabelWrapper>
            {intl.formatMessage({ id: "blackfire.title" })}
          </S.MainLabelWrapper>
          <S.AppSizeOption>
            {canPurchaseFromConsole ? (
              <Button
                variant="outline"
                onClick={handleFeatureToggle}
                analyticId={blackfire ? "remove" : "add"}
              >
                {blackfire
                  ? intl.formatMessage({ id: "remove" })
                  : intl.formatMessage({ id: "add" })}
              </Button>
            ) : isPurchased ? null : (
              <Button
                target="_blank"
                variant="outline"
                href={intl.formatMessage({ id: "links.platform.contact" })}
                analyticId="contact_sales"
              >
                {intl.formatMessage({ id: "contact_sales" })}
              </Button>
            )}
            <AmountField>
              {enterpriseIsPurchased && estimateCost?.amount === 0
                ? "Included"
                : estimateCost?.formatted}
            </AmountField>
          </S.AppSizeOption>

          <S.Price>
            {intl.formatMessage({
              id: isEnterprise
                ? "plans.blackfire.enterprize_addon_description"
                : "plans.blackfire.pro_addon_description"
            })}
          </S.Price>

          <S.DialogGroupDefault
            description={intl.formatMessage(
              {
                id: isEnterprise
                  ? getEnterpriseInfo(isPurchased)
                  : "plans.blackfire.pro_addon_acquire_info"
              },
              {
                blackfireSupportLanguages: "PHP and Python"
              }
            )}
            analyticId={
              isEnterprise
                ? getEnterpriseInfo(isPurchased)
                : "plans.blackfire.pro_addon_acquire_info"
            }
            link={
              <S.IntegrationActionLinkWrapper>
                {(isEnterprise
                  ? getEnterpriseActionLinks(isPurchased)
                  : actionLinksPro
                ).map(link => (
                  <Link
                    analyticId={link.title}
                    key={intl.formatMessage({ id: link.title })}
                    href={intl.formatMessage({ id: link.href })}
                  >
                    {intl.formatMessage({ id: link.title })}
                  </Link>
                ))}
              </S.IntegrationActionLinkWrapper>
            }
            trigger={
              <IconControl aria-label="info-trigger-pro-addon">
                <InfoSmIcon />
              </IconControl>
            }
          />
        </S.Info>
      </S.Heading>
      {blackfireAddedButNotPurchased && (
        <Sticker.Root>
          <Sticker.Icon priority="information" />
          <StickerText>
            {intl.formatMessage(
              {
                id: "plans.blackfire.addon_add_prompt_continue_description_note"
              },
              {
                a: chunks => (
                  <a
                    href={intl.formatMessage({
                      id: "blackfire.io"
                    })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {chunks}
                  </a>
                ),
                strong: chunks => <S.Note>{chunks}</S.Note>
              }
            )}
          </StickerText>
        </Sticker.Root>
      )}
      <Modal
        handleClose={() => {
          setAddBlackfire();
        }}
        onOpenChange={() => {}}
        isOpen={openAddPromptModal}
      >
        <PromptEnablObservabilitySuite />

        <ButtonWrapper spacing="modal" justifyContent="end">
          <Button
            variant="secondary"
            analyticId="cancel"
            onClick={() => {
              setOpenAddPromptModal(false);
            }}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            onClick={() => {
              setAddBlackfire(PLATFORM_FEATURE_BLACKFIRE);
            }}
            analyticId="enable"
          >
            {intl.formatMessage({ id: "enable" })}
          </Button>
        </ButtonWrapper>
      </Modal>
    </>
  );
};

export default BlackfireAddon;
